import { useLayoutEffect, useState } from 'react';

/** Run a chart card's shared CSS entrance once, when ready AND in view.
 * Observe additions/loading completion without remounting charts or losing state.
 */
export function usePageEntranceVisibility(ready: boolean) {
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const cards = new Set<HTMLElement>();
    const visible = new Set<Element>();
    const selector = '.charts-feature-grid > .chart-card, .charts-dashboard-grid > .chart-card, .charts-dashboard-grid > .charts-report-card, .mobile-charts-list > .chart-card, .mobile-charts-list > .charts-report-card';
    const reveal = (card: HTMLElement) => {
      if (card.dataset.pageEntry === 'entered') return;
      const busy = !ready || card.querySelector('.nm-loader, .nm-loading-overlay, .performance-widget__loader, [aria-busy="true"]');
      // Loading placeholders remain visible. Their entrance starts after loading.
      const next = busy ? 'loading' : visible.has(card) ? 'entered' : 'pending';
      if (card.dataset.pageEntry !== next) card.dataset.pageEntry = next;
      if (next === 'entered') observer.unobserve(card);
    };
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
        reveal(entry.target as HTMLElement);
      }
    }, { threshold: 0.01 });
    const sync = () => {
      const current = new Set(root.querySelectorAll<HTMLElement>(selector));
      for (const card of cards) {
        if (!current.has(card)) {
          observer.unobserve(card);
          visible.delete(card);
          cards.delete(card);
        }
      }
      for (const card of current) {
        if (!cards.has(card)) {
          cards.add(card);
          if (card.dataset.pageEntry !== 'entered') observer.observe(card);
        }
        reveal(card);
      }
    };
    sync();
    const mutations = new MutationObserver(sync);
    // Don't observe our own data attributes or chart hover/animation mutations.
    mutations.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-busy'] });
    return () => { observer.disconnect(); mutations.disconnect(); };
  }, [root, ready]);
  return setRoot;
}
